import { Component, Input, Output, EventEmitter } from '@angular/core';
import { ApprovalPoliciesService } from 'src/app/services/approval-policies.service';
import * as alertifyjs from 'alertifyjs';

@Component({
  selector: 'app-work-condition-master',
  templateUrl: './work-condition-master.component.html',
  styleUrls: ['./work-condition-master.component.css']
})
export class WorkConditionMasterComponent {
  @Input() allWorkConditions: string[] = [];
  @Input() availableWorkConditions: string[] = [];
  @Input() refusedWorkConditions: string[] = [];
  @Input() approvalGroups: any[] = [];
  @Output() policiesUpdated = new EventEmitter<void>();

  // Estado del componente
  newCondition = '';
  addingCondition = false;
  showAllConditions = false;
  showNewConditionSection = false; // Controla la visibilidad de la sección de nueva condición

  constructor(private approvalPoliciesService: ApprovalPoliciesService) {}

  /**
   * Obtener el grupo que usa una condición laboral específica
   */
  getGroupForCondition(condition: string): string | null {
    if (!this.approvalGroups || this.approvalGroups.length === 0) {
      return null;
    }

    for (const group of this.approvalGroups) {
      if (group.workingConditions && group.workingConditions.includes(condition)) {
        return group.name;
      }
    }

    return null;
  }

  /**
   * Verificar si una condición está en uso por algún grupo
   */
  isConditionInUse(condition: string): boolean {
    return this.getGroupForCondition(condition) !== null;
  }

  /**
   * Agregar nueva condición laboral al maestro
   */
  async addNewCondition(): Promise<void> {
    if (!this.newCondition.trim()) {
      alertifyjs.error('Por favor ingrese una condición laboral');
      return;
    }

    const condition = this.newCondition.trim();

    // Verificar que no exista ya
    if (this.allWorkConditions.includes(condition)) {
      alertifyjs.warning(`La condición "${condition}" ya existe en el sistema`);
      this.newCondition = '';
      return;
    }

    this.addingCondition = true;

    try {
      await this.approvalPoliciesService.addWorkCondition(condition);
      alertifyjs.success(`Condición laboral "${condition}" agregada exitosamente`);
      this.newCondition = '';
      this.policiesUpdated.emit();
    } catch (error: any) {
      console.error('Error agregando condición laboral:', error);
      alertifyjs.error(error.error?.message || 'Error al agregar condición laboral');
    } finally {
      this.addingCondition = false;
    }
  }

  /**
   * Mover condición de disponibles a rechazadas
   */
  async moveToRefused(condition: string): Promise<void> {
    try {
      await this.approvalPoliciesService.addWorkConditionToRefusedList(condition);
      alertifyjs.success(`Condición "${condition}" movida a rechazadas`);
      this.policiesUpdated.emit();
    } catch (error: any) {
      console.error('Error moviendo condición a rechazadas:', error);
      alertifyjs.error(error.error?.message || 'Error al mover condición a rechazadas');
    }
  }

  /**
   * Mover condición de rechazadas a disponibles
   */
  async moveFromRefused(condition: string): Promise<void> {
    try {
      await this.approvalPoliciesService.removeWorkConditionFromRefusedList(condition);
      alertifyjs.success(`Condición "${condition}" removida de rechazadas`);
      this.policiesUpdated.emit();
    } catch (error: any) {
      console.error('Error removiendo condición de rechazadas:', error);
      alertifyjs.error(error.error?.message || 'Error al remover condición de rechazadas');
    }
  }

  /**
   * Obtener color para el estado de la condición
   */
  getConditionStatusColor(condition: string): string {
    if (this.refusedWorkConditions.includes(condition)) {
      return '#dc3545'; // Rojo para rechazadas
    }
    if (this.availableWorkConditions.includes(condition)) {
      return '#28a745'; // Verde para disponibles
    }
    return '#6c757d'; // Gris para otras
  }

  /**
   * Obtener clase CSS para el estado de la condición
   */
  getConditionStatusClass(condition: string): string {
    if (this.refusedWorkConditions.includes(condition)) {
      return 'refused';
    }
    if (this.availableWorkConditions.includes(condition)) {
      return 'available';
    }
    return 'neutral';
  }

  /**
   * Verificar si una condición puede ser movida a rechazadas
   */
  canMoveToRefused(condition: string): boolean {
    return this.availableWorkConditions.includes(condition);
  }

  /**
   * Verificar si una condición puede ser removida de rechazadas
   */
  canMoveFromRefused(condition: string): boolean {
    return this.refusedWorkConditions.includes(condition);
  }

  /**
   * Obtener estadísticas
   */
  getStats() {
    return {
      total: this.allWorkConditions.length,
      available: this.availableWorkConditions.length,
      refused: this.refusedWorkConditions.length,
      used: this.allWorkConditions.length - this.availableWorkConditions.length - this.refusedWorkConditions.length
    };
  }

  /**
   * Toggle mostrar todas las condiciones
   */
  toggleShowAllConditions(): void {
    this.showAllConditions = !this.showAllConditions;
  }

  /**
   * Obtener estado de una condición (para mostrar en lista completa)
   */
  getConditionState(condition: string): string {
    if (this.refusedWorkConditions.includes(condition)) {
      return 'Rechazada';
    }
    if (this.availableWorkConditions.includes(condition)) {
      return 'Disponible';
    }
    return 'En Uso';
  }
}
